import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Check, X, Clock, Settings, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import PunchLocationMap from '../../../shared/components/ui/PunchLocationMap';
import {
  getSitePunchApprovals,
  decideSitePunch,
  getStaffAttendancePolicy,
  setStaffAttendancePolicy
} from '../../services/api/implementation/impl';
import { Callout } from '../../../shared/components/ui/primitives';

/**
 * Site punches waiting on a manager.
 *
 * The important thing this screen has to say is that NONE OF THESE DAYS HAS
 * COUNTED YET. A pending punch is recorded and deliberately carries no status,
 * so payroll passes over it — which is the whole point, and also the reason an
 * unattended queue quietly costs somebody their pay.
 *
 * The coordinate shown is the EMPLOYEE'S OWN — they punched themselves in — so
 * unlike the labour muster it does evidence where they were.
 */
const SitePunchApprovals = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=employees' });
  const queryClient = useQueryClient();
  const [refusing, setRefusing] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: ['sitePunchApprovals'],
    queryFn: () => getSitePunchApprovals()
  });

  const policy = useQuery({
    queryKey: ['staffAttendancePolicy'],
    queryFn: getStaffAttendancePolicy
  });

  const decide = useMutation({
    mutationFn: ({ id, ...payload }) => decideSitePunch(id, payload),
    onSuccess: (_r, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sitePunchApprovals'] });
      setRefusing(null);
      toast.success(
        variables.approve === false
          ? 'Refused'
          : 'Confirmed — the day now counts as attendance'
      );
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not record that')
  });

  const rows = data?.data ?? [];
  const reviewOn = policy.data?.sitePunchRequiresApproval ?? policy.data?.site_punch_requires_approval;

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Site Punch Approvals' }} />

      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <Callout tone="info" collapsible icon={<Info size={14} />} title="About site punches" className="min-w-0 flex-1 sm:max-w-2xl">
            Punches made away from an office. They don&apos;t count as attendance — or in payroll — until you approve them.
          </Callout>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
          >
            <Settings size={14} /> Rules
          </button>
        </div>

        {reviewOn === false && (
          <div className="rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
            Site punches are <strong>not</strong> being reviewed — they count the
            moment they are made. Nothing will ever appear here until you turn
            review on under Rules.
          </div>
        )}

        {isFetching ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center">
            <Check size={28} className="mx-auto text-success-fg" />
            <p className="mt-2 text-sm text-gray-600">Nothing waiting.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.id} className="rounded-lg border border-warning-border bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {row.user?.name ?? 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1 rounded bg-warning-bg px-1.5 py-0.5 text-[10px] font-semibold uppercase text-warning-fg">
                        <Clock size={9} /> Not counted yet
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {shortDate(row.date)} · in {clock(row.check_in)}
                      {row.check_out ? ` · out ${clock(row.check_out)}` : ' · still in'}
                      {row.worked_hours ? ` · ${Number(row.worked_hours)}h` : ''}
                    </div>
                    {row.site_note ? (
                      <p className="mt-2 flex items-start gap-1.5 text-sm text-gray-700">
                        <MapPin size={14} className="mt-0.5 shrink-0 text-gray-400" />
                        {row.site_note}
                      </p>
                    ) : (
                      // Not framed as a failing. The remark is optional, and a
                      // reviewer has the map, the times and who it is.
                      <p className="mt-2 text-sm italic text-gray-500">
                        No remark left.
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={decide.isPending}
                      onClick={() => decide.mutate({ id: row.id, approve: true })}
                      className="flex items-center gap-1 rounded bg-brand-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                    >
                      <Check size={13} /> Confirm
                    </button>
                    <button
                      type="button"
                      disabled={decide.isPending}
                      onClick={() => setRefusing(row)}
                      className="flex items-center gap-1 rounded border border-danger-border px-3 py-1.5 text-xs text-danger-fg disabled:opacity-50"
                    >
                      <X size={13} /> Refuse
                    </button>
                  </div>
                </div>

                {row.location?.lat && (
                  <div className="mt-3">
                    <PunchLocationMap
                      height={200}
                      points={[
                        {
                          lat: row.location.lat,
                          lng: row.location.lng,
                          accuracy_m: row.location.accuracy_m,
                          label: 'Punched in',
                          time: clock(row.check_in),
                          kind: 'IN'
                        }
                      ]}
                      caption="Recorded on this employee's own device when they punched in."
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {refusing && (
        <RefuseModal
          row={refusing}
          busy={decide.isPending}
          onClose={() => setRefusing(null)}
          onSubmit={(reason) => decide.mutate({ id: refusing.id, approve: false, reason })}
        />
      )}

      {settingsOpen && (
        <RulesModal
          policy={policy.data}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['staffAttendancePolicy'] });
            queryClient.invalidateQueries({ queryKey: ['sitePunchApprovals'] });
            setSettingsOpen(false);
          }}
        />
      )}
    </Layout>
  );
};

const RefuseModal = ({ row, busy, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  return (
    <Modal name={`Refuse ${row.user?.name ?? 'this'} site punch`} onClose={onClose} width="460px">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          The punch stays on record, marked refused, and they are told why.
          Deleting it would remove the evidence of a day that is being disputed.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          placeholder="No site visit was scheduled for this day"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !reason.trim()}
            onClick={() => onSubmit(reason.trim())}
            className="rounded-lg bg-danger px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy ? 'Recording…' : 'Refuse'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const RulesModal = ({ policy, onClose, onSaved }) => {
  const [approval, setApproval] = useState(
    Boolean(policy?.site_punch_requires_approval ?? policy?.sitePunchRequiresApproval)
  );
  const [note, setNote] = useState(
    policy?.site_punch_requires_note ?? policy?.sitePunchRequiresNote ?? true
  );

  const save = useMutation({
    mutationFn: setStaffAttendancePolicy,
    onSuccess: (result) => {
      toast.success(result?.message || 'Saved');
      onSaved();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save')
  });

  return (
    <Modal name="Site punch rules" onClose={onClose} width="520px">
      <div className="space-y-3">
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={approval}
            onChange={(e) => setApproval(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-brand-700"
          />
          <span>
            Site punches must be confirmed by a manager
            <span className="block text-xs text-gray-500">
              Until confirmed, the day is recorded but does not count as
              attendance and is not paid.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={note}
            disabled={!approval}
            onChange={(e) => setNote(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-brand-700 disabled:opacity-40"
          />
          <span>
            Require a remark on every site punch
            <span className="block text-xs text-gray-500">
              Off by default — somebody leaving home for a meeting punches on
              their way out of the door, and a mandatory box there makes people
              punch in late. The remark is always offered either way.
            </span>
          </span>
        </label>

        {/* The consequence stated before they turn it on, not discovered at
            payroll three weeks later. */}
        {approval && (
          <p className="rounded border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
            Turning this on affects everyone who punches in from outside an
            office — including field and sales staff. Somebody has to work the
            queue, or their days stop counting.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                site_punch_requires_approval: approval,
                site_punch_requires_note: note
              })
            }
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

function clock(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

export default SitePunchApprovals;
